'use client';

import React from 'react';
import { ElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react';
import Image from 'next/image';
import DynamicImage from '@/components/commons/dynamic-image';
import { cn } from '@/lib/utils';

type ModalImagePreviewProps = {
  content?: ElementDataType;
  images: string[];
  initialIndex?: number;
  isOpen?: boolean;
  onClose?: () => void;
};

export default function ModalImagePreview({
  content,
  images,
  initialIndex = 0,
  isOpen,
  onClose,
}: ModalImagePreviewProps) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [scale, setScale] = React.useState(1);
  const positionRef = React.useRef({ x: 0, y: 0 });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [startPos, setStartPos] = React.useState<{ x: number; y: number } | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const thumbnailsRef = React.useRef<HTMLDivElement>(null);

  const updateIndex = (newIndex: number) => {
    setCurrentIndex((newIndex + images.length) % images.length);
    resetZoom();
  };

  const zoom = (direction: 1 | -1) => {
    setScale((prev) => {
      const newScale = Math.max(1, Math.min(3, prev + 0.5 * direction));
      if (newScale === 1 && direction === -1) resetZoom();
      return newScale;
    });
  };

  const resetZoom = () => {
    setScale(1);
    positionRef.current = { x: 0, y: 0 };
    setPosition({ x: 0, y: 0 });
  };

  const handleDrag = React.useCallback(
    (dx: number, dy: number) => {
      if (scale > 1) {
        positionRef.current.x = Math.max(-300, Math.min(300, positionRef.current.x + dx));
        positionRef.current.y = Math.max(-300, Math.min(300, positionRef.current.y + dy));

        if (!animationFrameRef.current) {
          animationFrameRef.current = requestAnimationFrame(() => {
            setPosition({ ...positionRef.current });
            animationFrameRef.current = null;
          });
        }
      }
    },
    [scale]
  );

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (isDragging && startPos) {
        handleDrag(e.clientX - startPos.x, e.clientY - startPos.y);
        setStartPos({ x: e.clientX, y: e.clientY });
      }
    },
    [isDragging, startPos, handleDrag]
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchMove = React.useCallback(
    (e: TouchEvent) => {
      if (startPos) {
        const touch = e.touches[0];
        handleDrag(touch.clientX - startPos.x, touch.clientY - startPos.y);
        setStartPos({ x: touch.clientX, y: touch.clientY });
      }
    },
    [startPos, handleDrag]
  );

  const handleClose = () => {
    resetZoom();
    if (onClose) onClose();
  };

  React.useEffect(() => {
    if (thumbnailsRef.current) {
      const activeThumbnail = thumbnailsRef.current.children[currentIndex] as HTMLElement;
      activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentIndex]);

  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', () => setStartPos(null));
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', () => setStartPos(null));
    };
  }, [handleTouchMove]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div className='fixed inset-0 z-[200] flex items-center justify-center bg-white'>
      <div className='relative h-full w-full'>
        <Button
          variant='ghost'
          size='icon'
          className='absolute right-4 top-4 z-10 bg-white/10'
          onClick={handleClose}
        >
          <X className='h-6 w-6' />
        </Button>

        <div className='relative h-[calc(100vh-100px)] overflow-hidden bg-white'>
          <div
            ref={containerRef}
            className={`relative h-full ${scale > 1 ? 'cursor-move' : ''}`}
            onMouseDown={(e) => {
              if (scale > 1) {
                setIsDragging(true);
                setStartPos({ x: e.clientX, y: e.clientY });
              }
            }}
            onTouchStart={(e) => setStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY })}
          >
            <DynamicImage
              src={images[currentIndex] || '/placeholder.svg'}
              alt={`Product image ${currentIndex + 1}`}
              fill
              className='object-contain'
              style={{
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s',
              }}
              priority
              draggable={false}
            />
          </div>

          <div className='absolute right-4 top-20 space-y-2'>
            <Button
              variant='ghost'
              size='icon'
              className='bg-white/10'
              onClick={() => zoom(1)}
              disabled={scale === 3}
            >
              <ZoomIn className='h-6 w-6' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='bg-white/10'
              onClick={() => zoom(-1)}
              disabled={scale === 1}
            >
              <ZoomOut className='h-6 w-6' />
            </Button>
          </div>

          <Button
            variant='ghost'
            size='icon'
            className='absolute left-4 top-1/2 bg-white/10'
            onClick={() => updateIndex(currentIndex - 1)}
          >
            <ChevronLeft className='h-6 w-6' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='absolute right-4 top-1/2 bg-white/10'
            onClick={() => updateIndex(currentIndex + 1)}
          >
            <ChevronRight className='h-6 w-6' />
          </Button>
        </div>

        <div className='absolute bottom-0 left-0 right-0 bg-white/50 backdrop-blur-sm'>
          <div className='relative w-full overflow-x-auto'>
            <div
              ref={thumbnailsRef}
              className='relative mx-auto w-max gap-2 p-4'
              style={{ justifyContent: images.length > 15 ? 'flex-start' : 'center' }}
            >
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => updateIndex(index)}
                  className={cn(
                    'relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2',
                    index !== currentIndex ? 'border-transparent' : ''
                  )}
                  style={
                    index === currentIndex
                      ? { borderColor: content?.colorSchemeProductDetail?.textSecondary }
                      : undefined
                  }
                >
                  <Image
                    src={image || '/placeholder.svg'}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    className='object-cover'
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
